import React from 'react';
import styled from 'styled-components';
import { FormControlLabel, InfoBox, Typography } from '@beeline.com/bp-design-system';
import { RadioGroup, Radio } from '@beeline.com/bp-design-system';
import { useImportersIntl } from '../../../translations/useImportersIntl';

export type PendingChangesOption = 'discardAll' | 'skipPending';

interface Props {
  onOptionChange: (option: PendingChangesOption) => void;
  selectedOption: PendingChangesOption | null;
  showValidation: boolean;
}

export const PendingChangesAlert = ({ onOptionChange, selectedOption, showValidation }: Props) => {
  const intl = useImportersIntl();

  return (
    <StyledInfoBox type="ALERT" title={intl.engagementWithPendingChangesDetected} iconAlign="flex-start">
      <RadioGroup
        name="pending-changes-option"
        value={selectedOption ?? ''}
        onChange={(_e, value) => onOptionChange(value as PendingChangesOption)}
      >
        <StyledFormControlLabel
          value="discardAll"
          control={<Radio color="primary" dataTest="pending-changes-discard-all" />}
          label={
            <StyledOptionLabel>
              <Typography variant="label1">{intl.importAllAndDiscardPendingChanges}</Typography>
              <Typography variant="body1" color="colorOnSurfaceVariant">
                {intl.allRelatedApprovalProcessesWillBeTerminated}
              </Typography>
            </StyledOptionLabel>
          }
        />
        <StyledFormControlLabel
          value="skipPending"
          control={<Radio color="primary" dataTest="pending-changes-skip-pending" />}
          label={
            <StyledOptionLabel>
              <Typography variant="label1">{intl.onlyImportEngagementsWithoutPendingChanges}</Typography>
              <Typography variant="body1" color="colorOnSurfaceVariant">
                {intl.importEngagementsWithoutPendingChangesWillBeSkipped}
              </Typography>
            </StyledOptionLabel>
          }
        />
      </RadioGroup>
      {showValidation && (
        <StyledValidationMessage variant="body1">{intl.selectAnOptionToContinue}</StyledValidationMessage>
      )}
    </StyledInfoBox>
  );
};

const StyledInfoBox = styled(InfoBox)`
  margin-top: 8px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  align-items: flex-start;
  margin-top: 8px;

  .MuiFormControlLabel-label {
    padding-top: 2px;
  }
`;

const StyledOptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledValidationMessage = styled(Typography)`
  && {
    color: ${({ theme }) => theme.colorAlert};
    margin-top: 8px;
  }
`;
